import { Fragment } from "react";
import { History, ShieldAlert } from "lucide-react";
import { AppLink, Button, Callout, EmptyState, Skeleton, formatDateTime, isApiError } from "@amber/ui";
import { formatSourceDate } from "@amber/collection/ui";
import type { EvidenceRecord } from "../../contracts/index";
import { languageName } from "../format";

const UNAVAILABLE: Record<number, { title: string; text: string }> = {
  403: { title: "No access", text: "This record is restricted. Only Admins can open it." },
  404: { title: "Record not found", text: "No item with this ID or version, or it isn't visible to your role." },
};
const LOAD_FAILED = { title: "Couldn't load the record", text: "Try again in a moment." };

export function RecordUnavailable({ isError, error }: { isError: boolean; error: unknown }) {
  if (isError) {
    const status = isApiError(error) ? error.status : 0;
    const notice = UNAVAILABLE[status] ?? LOAD_FAILED;
    return (
      <div className="p-6">
        <EmptyState level={1} icon={<ShieldAlert aria-hidden="true" />} title={notice.title}>
          {notice.text}
        </EmptyState>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3 p-4" aria-busy="true">
      <Skeleton className="h-6 w-1/2" />
      <Skeleton className="h-[420px] w-full" />
    </div>
  );
}

export function PinnedBanner({ record, onLatest }: { record: EvidenceRecord; onLatest: () => void }) {
  if (!record.pinned) return null;
  return (
    <div role="status" className="flex items-center gap-3 border-b border-divider bg-waiting-soft px-4 py-2 text-md">
      <History size={14} aria-hidden="true" />
      <span>
        You're viewing an older version · Transcript as of {formatDateTime(record.pinned.published_at)} · read-only
      </span>
      <button type="button" className="font-strong text-link" onClick={onLatest}>
        Back to latest
      </button>
    </div>
  );
}

export function RestrictionBanner({ record, onRestore }: { record: EvidenceRecord; onRestore: () => void }) {
  if (!record.restriction) return null;
  return (
    <div role="status" className="flex items-center gap-3 border-b border-divider bg-attention-soft px-4 py-2 text-md">
      <ShieldAlert size={14} aria-hidden="true" className="text-attention" />
      <span>
        <b>{record.restriction.state === "deleted" ? "Deleted" : "Restricted"}</b>
        {record.restriction.by ? ` by ${record.restriction.by}` : ""}
        {record.restriction.at ? ` · ${formatDateTime(record.restriction.at)}` : ""}
        {record.restriction.reason ? ` · “${record.restriction.reason}”` : ""} · visible to Admins only
      </span>
      <Button size="sm" onClick={onRestore}>
        {record.restriction.state === "deleted" ? "Restore…" : "Lift restriction…"}
      </Button>
      <AppLink href="/settings/deleted" className="text-link">
        Settings › Deleted items
      </AppLink>
    </div>
  );
}

export function FailedPanel({ record }: { record: EvidenceRecord }) {
  const f = record.failure;
  if (!f) return null;
  const known: [string, string][] = [
    ["Platform", record.item.platform.label],
    ["Channel", record.item.channel ?? "Unknown"],
    ["Published", record.item.dates.published ? formatSourceDate(record.item.dates.published) : "Unknown"],
    ["Discovered", formatDateTime(record.item.dates.discovered_at)],
    ["Language", languageName(record.item.language)],
  ];
  return (
    <div className="flex flex-col gap-3 overflow-y-auto p-4">
      <Callout tone="attention" title={f.title}>
        {f.reason ? <p className="m-0">{f.reason}</p> : null}
        {f.next ? <p className="m-0 mt-1">Next step: {f.next}</p> : null}
        {f.kept ? <p className="m-0 mt-1">{f.kept}.</p> : null}
      </Callout>
      {f.attempts.length > 0 ? (
        <section aria-label="Attempts">
          <h3 className="m-0 mb-1 text-ui font-semibold">Attempts</h3>
          <ul className="m-0 list-none p-0 text-md">
            {f.attempts.map((a) => (
              <li key={`${String(a.number)}-${a.at}`} className="flex gap-2 border-b border-divider py-1.5">
                <span className="font-mono">
                  {a.number} of {a.of}
                </span>
                <span className="text-muted">{formatDateTime(a.at)}</span>
                <span>{(a.outcome ?? "").replace(/_/g, " ")}</span>
                {a.route ? <span className="text-muted">· {a.route}</span> : null}
                <span className="min-w-0 flex-1 truncate text-muted" title={a.detail ?? undefined}>
                  {a.detail}
                </span>
                {a.engine_href ? (
                  <a href={a.engine_href} target="_blank" rel="noreferrer noopener" className="text-link">
                    Engine run
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {record.transcript ? null : (
        <section aria-label="Known from discovery">
          <h3 className="m-0 mb-1 text-ui font-semibold">Known from discovery</h3>
          <dl className="m-0 grid grid-cols-[90px_1fr] gap-y-1 text-md [&_dd]:m-0 [&_dt]:text-muted">
            {known.map(([k, v]) => (
              <Fragment key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </Fragment>
            ))}
          </dl>
        </section>
      )}
    </div>
  );
}
